import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Bike, Zap, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import sustainabilityImage from '@/assets/sustainability-new.webp';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

export function SustainabilityPreview() {
  const t = useT();
  const initiatives = [
    {
      icon: Zap,
      title: t('Ökostrom', 'Green electricity'),
      description: t('Produktion mit 100% Wasserkraft von Lichtblick', 'Production powered by 100 % hydroelectricity'),
    },
    {
      icon: Bike,
      title: t('Lastenrad-Lieferung', 'Cargo-bike delivery'),
      description: t('CO₂-freie Lieferung an regionale Kunden', 'CO₂-free delivery to regional customers'),
    },
    {
      icon: Globe,
      title: t('BNW Mitglied', 'BNW member'),
      description: t('Aktiv im Bundesverband Nachhaltige Wirtschaft', 'Active in the German Federal Association for Sustainable Economy'),
    },
    {
      icon: Leaf,
      title: t('SDG-Ziele', 'SDG goals'),
      description: t('Ausrichtung an UN-Nachhaltigkeitszielen', 'Aligned with the UN Sustainable Development Goals'),
    },
  ];
  return (
    <section className="py-24 bg-gradient-to-b from-card to-background relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            className="relative rounded-2xl overflow-hidden"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src={sustainabilityImage}
              alt={t('Nachhaltige Fertigung', 'Sustainable manufacturing')}
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            
            {/* Overlay Badge */}
            <div className="absolute bottom-6 left-6 right-6">
              <div className="bg-background/90 backdrop-blur-sm rounded-lg p-4 border border-border/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <div className="font-semibold">Entrepreneurs For Future</div>
                    <div className="text-sm text-muted-foreground">
                      {t('Unterzeichner des Pariser Klimaabkommens', 'Signatory of the Paris Climate Agreement')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <div>
            <motion.span
              className="text-success font-mono text-sm uppercase tracking-wider"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {t('Verantwortung übernehmen', 'Taking responsibility')}
            </motion.span>
            
            <motion.h2
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('Nachhaltige ', 'Sustainable ')}
              <span className="text-success">{t('Fertigung', 'manufacturing')}</span>
            </motion.h2>
            
            <motion.p
              className="mt-6 text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t(
                'Was bedeutet Nachhaltigkeit in der Stahlverarbeitung? Wir analysieren unseren CO₂-Ausstoß, Energieverbrauch und die Herkunft der Materialien, um ein besseres Bild unserer Umweltauswirkungen zu erhalten.',
                'What does sustainability mean in metalworking? We analyse our CO₂ emissions, energy use and material origins to better understand our environmental impact.',
              )}
            </motion.p>

            {/* Initiatives */}
            <motion.div
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {initiatives.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50 border border-border/30"
                >
                  <div className="w-8 h-8 rounded bg-success/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-success" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Button variant="steel" size="lg" asChild>
                <LocalizedLink to="/nachhaltigkeit">
                  {t('Mehr erfahren', 'Learn more')}
                  <ArrowRight className="w-4 h-4" />
                </LocalizedLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
